import { Fragment, type ReactNode } from "react";
import { Box, Typography } from "@mui/material";

/** Render **bold**, *italic*, simple headings, and bullet lists (no extra dependencies). */
function MarkdownInline({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return (
    <>
      {parts.map((part, i) => {
        const bold = part.match(/^\*\*(.+)\*\*$/);
        if (bold) {
          return (
            <Box component="strong" key={i} sx={{ fontWeight: 700 }}>
              {bold[1]}
            </Box>
          );
        }
        const italic = part.match(/^\*([^*]+)\*$/);
        if (italic) {
          return (
            <Box component="em" key={i} sx={{ fontStyle: "italic" }}>
              {italic[1]}
            </Box>
          );
        }
        return <Fragment key={i}>{part}</Fragment>;
      })}
    </>
  );
}

function isBulletLine(line: string): boolean {
  return /^[-*•]\s+/.test(line.trim());
}

function isHeadingLine(line: string): boolean {
  return /^\s*#{1,4}\s+/.test(line) || /^\s*\*\*[^*]+\*\*\s*:?\s*$/.test(line.trim());
}

function headingText(line: string): string {
  const h = line.match(/^\s*(#{1,4})\s+(.+)$/);
  if (h) return h[2].replace(/\*\*/g, "").trim();
  return line.replace(/^\*\*|\*\*$/g, "").replace(/:$/, "").trim();
}

function bulletBody(line: string): string {
  return line.replace(/^[-*•]\s+/, "").trim();
}

function renderHeading(bi: number, line: string): ReactNode {
  return (
    <Typography
      key={`h-${bi}`}
      variant="subtitle2"
      fontWeight={700}
      sx={{ mt: bi ? 2 : 0, mb: 1, color: "text.primary" }}
    >
      <MarkdownInline text={headingText(line)} />
    </Typography>
  );
}

function renderBulletList(bulletLines: string[], key: string): ReactNode {
  return (
    <Box component="ul" key={key} sx={{ pl: 2.5, my: 1, "& li": { mb: 0.75 } }}>
      {bulletLines.map((line, li) => (
        <Box component="li" key={li}>
          <Typography variant="bodyNormal" component="span">
            <MarkdownInline text={bulletBody(line)} />
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

interface RagMarkdownContentProps {
  text: string;
  /** Preserve single newlines inside paragraphs (e.g. step prose). */
  dense?: boolean;
}

/**
 * Lightweight markdown for assistant answers (bold, headings, bullets).
 * Page citations [p.N] are left intact for upstream parsers.
 */
export function RagMarkdownContent({ text, dense = false }: RagMarkdownContentProps) {
  if (!text.trim()) return null;

  const blocks = dense ? [text] : text.split(/\n\n+/);
  const nodes: ReactNode[] = [];

  blocks.forEach((block, bi) => {
    const lines = block.split("\n").map((l) => l.trimEnd()).filter((l, idx, arr) => {
      if (idx === 0 || l.trim()) return true;
      return arr.slice(idx).some((x) => x.trim());
    });

    if (!lines.length) return;

    const bulletLines = lines.filter((l) => isBulletLine(l));
    const allBullets = bulletLines.length > 0 && bulletLines.length === lines.filter((l) => l.trim()).length;

    if (allBullets) {
      nodes.push(renderBulletList(bulletLines, `b-${bi}`));
      return;
    }

    const firstLine = lines[0];
    const restLines = lines.slice(1).filter((l) => l.trim());
    const restBullets = restLines.filter((l) => isBulletLine(l));

    // e.g. **Possible Causes:**\n- cause one\n- cause two
    if (
      lines.length > 1 &&
      isHeadingLine(firstLine) &&
      restBullets.length > 0 &&
      restBullets.length === restLines.length
    ) {
      nodes.push(renderHeading(bi, firstLine));
      nodes.push(renderBulletList(restBullets, `hb-${bi}`));
      return;
    }

    // e.g. **Description:**\nParagraph text…
    if (lines.length > 1 && isHeadingLine(firstLine) && restLines.length > 0) {
      nodes.push(renderHeading(bi, firstLine));
      nodes.push(
        <Typography
          key={`hp-${bi}`}
          variant="bodyNormal"
          component="div"
          sx={{ whiteSpace: "pre-wrap", mb: dense ? 0.5 : 1.25, lineHeight: 1.6 }}
        >
          <MarkdownInline text={restLines.join(dense ? "\n" : " ")} />
        </Typography>
      );
      return;
    }

    if (lines.length === 1 && isHeadingLine(lines[0])) {
      nodes.push(renderHeading(bi, lines[0]));
      return;
    }

    const paragraph = lines.join(dense ? "\n" : " ");
    nodes.push(
      <Typography
        key={`p-${bi}`}
        variant="bodyNormal"
        component="div"
        sx={{ whiteSpace: "pre-wrap", mb: dense ? 0.5 : 1.25, lineHeight: 1.6 }}
      >
        <MarkdownInline text={paragraph} />
      </Typography>
    );
  });

  return <Box>{nodes}</Box>;
}
